/*!
 * Rover Quick-Search — tier-1 instant typeahead for RoverParts.eu
 * ----------------------------------------------------------------
 * A small, framework-free dropdown that attaches to the existing Rover search inputs
 * (#q-main / #q-results / #q-zero / #rover-sticky-q) and returns very confident, literal
 * matches against part codes and product names as the customer types. Pressing Enter (or
 * Search) hands off to the existing tier-2 AI search — window.Rover.search(query) — unchanged.
 *
 * Runs PARALLEL to SoBooster and is fully feature-flagged: if window.RoverQS.enabled is
 * false the whole component is inert, so SoBooster stays the instant search and nothing
 * regresses. Treats every query and the index blob strictly as data.
 *
 * Matching mirrors the box oracle (rover_engine/suggest.py) exactly so the in-browser path
 * and the /v1/suggest fallback rank identically. No external dependencies.
 */
(function () {
  "use strict";

  var CFG = window.RoverQS || {};
  if (CFG.enabled === false) return;                       // feature flag — instant kill switch

  var BASE = (CFG.proxyBase || "/apps/rover").replace(/\/+$/, "");
  var INPUTS = CFG.inputSelector || "#q-main, #q-results, #q-zero, #rover-sticky-q";
  var LIMIT = Math.max(1, Math.min(CFG.limit || 8, 8));
  var DEBOUNCE = CFG.debounceMs || 150;
  var MIN_CHARS = CFG.minChars || 2;
  var MAX_SCAN = 5000;
  var RECENT_KEY = "roverqs_recent";
  var RECENT_MAX = 6;

  // ---- i18n (EN/NL) ---------------------------------------------------------------------------
  var T = {
    en: { codes: "Part codes", products: "Products", recent: "Recent searches",
          popular: "Popular products", all: "Search all results for", in_stock: "In stock",
          low_stock: "Low stock", out_of_stock: "Out of stock" },
    nl: { codes: "Artikelcodes", products: "Producten", recent: "Recente zoekopdrachten",
          popular: "Populaire producten", all: "Zoek alle resultaten voor", in_stock: "Op voorraad",
          low_stock: "Weinig voorraad", out_of_stock: "Niet op voorraad" }
  };
  function lang() {
    if (CFG.lang === "en" || CFG.lang === "nl") return CFG.lang;
    var app = document.getElementById("rover-app");
    var l = (app && app.getAttribute("data-lang")) || document.documentElement.lang ||
            navigator.language || "en";
    return l.toLowerCase().indexOf("nl") === 0 ? "nl" : "en";
  }
  function t(k) { return (T[lang()] || T.en)[k]; }
  function stockLabel(state) { return t(state) || ""; }

  // ---- normalisation (identical to vocab.norm_code/base_code + suggest.code_key/fold) ----------
  function normCode(c) { return (c == null ? "" : String(c)).replace(/\s/g, "").toUpperCase(); }   // delimiter-SENSITIVE
  function codeKey(c) { return (c == null ? "" : String(c)).toUpperCase().replace(/[^A-Z0-9]/g, ""); }
  function baseCode(c) {
    var n = (c == null ? "" : String(c)).toUpperCase().replace(/\s/g, "").replace(/[^A-Z0-9]/g, "");
    return n.replace(/([0-9])[A-Z]$/, "$1");
  }
  function fold(s) {
    if (!s) return "";
    return String(s).normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
  }
  var TOK = /[a-z0-9]+/g;
  function toks(s) { return (fold(s).match(TOK) || []); }

  var KIND_PRIO = { code_primary: 0, code_alt: 1, code_variant: 2, code_prefix: 3,
                    title_prefix: 4, title_token: 5, desc: 6 };
  function groupOf(kind) { return KIND_PRIO[kind] <= 3 ? "codes" : "products"; }
  function prio(kind) { return KIND_PRIO[kind] == null ? 99 : KIND_PRIO[kind]; }
  function stockRank(s) { return s === "in_stock" ? 0 : (s === "low_stock" ? 1 : 2); }

  // ---- in-browser index over the slim blob -----------------------------------------------------
  // Blob item keys: c=item_code(id+match) d=customer_code(display) t=title h=handle u=url k=codes[] p=price_display s=stock i=thumb v=pop
  var IDX = null;             // built index or null (then we use the server fallback)
  var BLOB_STATE = "idle";    // idle | loading | ready | error

  function buildIndex(items) {
    var norm = Object.create(null);    // normCode (delimiter-sensitive) -> [{i,tier}]
    var exact = Object.create(null);   // codeKey -> [{i,tier}]
    var base = Object.create(null);    // baseCode -> [i]
    var codeRows = [];                 // [key, i] sorted -> code prefix
    var titleRows = [];                // [ft, i] sorted -> title prefix
    var tokMap = Object.create(null);  // token -> [i]
    var enriched = new Array(items.length);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var nic = codeKey(it.c);
      var seen = Object.create(null), seenN = Object.create(null);
      var codes = [it.c].concat(it.k || []);
      for (var j = 0; j < codes.length; j++) {
        var nn = normCode(codes[j]);
        var ck = codeKey(codes[j]);
        var tier = ck === nic ? 0 : 1;
        if (nn && !seenN[nn]) { seenN[nn] = 1; (norm[nn] || (norm[nn] = [])).push({ i: i, tier: tier }); }
        if (!ck || seen[ck]) continue;
        seen[ck] = 1;
        (exact[ck] || (exact[ck] = [])).push({ i: i, tier: tier });
        codeRows.push([ck, i]);
        var b = baseCode(codes[j]);
        if (b) (base[b] || (base[b] = [])).push(i);
      }
      var ft = fold(it.t || "");
      if (ft) {
        titleRows.push([ft, i]);
        var seent = Object.create(null), ts = ft.match(TOK) || [];
        for (var k = 0; k < ts.length; k++) {
          if (seent[ts[k]]) continue;
          seent[ts[k]] = 1;
          (tokMap[ts[k]] || (tokMap[ts[k]] = [])).push(i);
        }
      }
      enriched[i] = it;
    }
    codeRows.sort(cmp0);
    titleRows.sort(cmp0);
    var popular = items.map(function (_it, i) { return i; })
      .sort(function (a, b2) { return (items[b2].v || 0) - (items[a].v || 0); })
      .slice(0, 20);
    return { items: enriched, norm: norm, exact: exact, base: base, codeRows: codeRows,
             titleRows: titleRows, tokMap: tokMap, tokKeys: Object.keys(tokMap).sort(),
             popular: popular };
  }
  function cmp0(a, b) { return a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : 0); }

  function lowerBound(rows, key) {
    var lo = 0, hi = rows.length;
    while (lo < hi) { var m = (lo + hi) >> 1; if (rows[m][0] < key) lo = m + 1; else hi = m; }
    return lo;
  }
  function prefixScan(rows, key, cb) {
    if (!key) return;
    var i = lowerBound(rows, key), n = 0;
    while (i < rows.length && rows[i][0].lastIndexOf(key, 0) === 0) {
      cb(rows[i][1]); i++;
      if (++n >= MAX_SCAN) break;
    }
  }

  // Local matcher — mirrors suggest.suggest(). Returns ranked slim rows with match_kind/group.
  function matchLocal(q) {
    if (!IDX) return null;
    q = (q || "").trim().slice(0, 64);
    if (q.length < MIN_CHARS) return [];
    var nn = normCode(q), nq = codeKey(q), fq = fold(q), qtoks = fq.match(TOK) || [];
    var best = Object.create(null);              // i -> kind
    function consider(i, kind) {
      var cur = best[i];
      if (cur === undefined || prio(kind) < prio(cur)) best[i] = kind;
    }
    // exact: delimiter-SENSITIVE first (retrieval parity), else delimiter-FOLDED (SoBooster fix)
    var ex = IDX.norm[nn] || IDX.exact[nq];
    if (ex) for (var a = 0; a < ex.length; a++) consider(ex[a].i, ex[a].tier === 0 ? "code_primary" : "code_alt");
    var hasExact = false;
    for (var bi in best) { if (prio(best[bi]) <= 1) { hasExact = true; break; } }
    if (!hasExact) {
      var bk = baseCode(q), arr = bk && IDX.base[bk];
      if (arr) for (var v = 0; v < arr.length; v++) consider(arr[v], "code_variant");
    }
    prefixScan(IDX.codeRows, nq, function (i) { consider(i, "code_prefix"); });
    prefixScan(IDX.titleRows, fq, function (i) { consider(i, "title_prefix"); });
    tokenMatch(qtoks).forEach(function (i) { consider(i, "title_token"); });

    var out = [];
    for (var idx in best) out.push([parseInt(idx, 10), best[idx]]);
    out.sort(function (x, y) {
      var px = prio(x[1]), py = prio(y[1]);
      if (px !== py) return px - py;
      var nx = IDX.items[x[0]].n || 0, ny = IDX.items[y[0]].n || 0;   // most stock first (avoid back-orders)
      if (nx !== ny) return ny - nx;
      var vx = IDX.items[y[0]].v || 0, vy = IDX.items[x[0]].v || 0;
      if (vx !== vy) return vx - vy;
      return IDX.items[x[0]].c < IDX.items[y[0]].c ? -1 : 1;
    });
    return out.slice(0, LIMIT).map(function (r) { return slim(IDX.items[r[0]], r[1]); });
  }
  function tokenMatch(qtoks) {
    if (!qtoks.length) return [];
    var sets = [], last = qtoks[qtoks.length - 1];
    for (var i = 0; i < qtoks.length - 1; i++) {
      var s = IDX.tokMap[qtoks[i]];
      if (!s) return [];
      sets.push(s);
    }
    var lastSet = Object.create(null), keys = IDX.tokKeys, j = lowerBound2(keys, last);
    while (j < keys.length && keys[j].lastIndexOf(last, 0) === 0) {
      IDX.tokMap[keys[j]].forEach(function (i) { lastSet[i] = 1; }); j++;
    }
    sets.push(Object.keys(lastSet).map(Number));
    var counts = Object.create(null), need = sets.length, res = [];
    sets.forEach(function (arr) {
      var seen = Object.create(null);
      arr.forEach(function (i) {
        if (seen[i]) return; seen[i] = 1;
        counts[i] = (counts[i] || 0) + 1;
        if (counts[i] === need) res.push(i);
      });
    });
    return res;
  }
  function lowerBound2(keys, key) {
    var lo = 0, hi = keys.length;
    while (lo < hi) { var m = (lo + hi) >> 1; if (keys[m] < key) lo = m + 1; else hi = m; }
    return lo;
  }
  function slim(it, kind) {
    // same key names as the server /v1/suggest rows, so optHTML renders one shape.
    // code = the customer-facing headline (blob `d` = customer_code); id stays item_code (`c`) for
    // identity/telemetry — matching still indexes `c` + all_codes, so the display change is render-only.
    return { id: it.c, code: it.d || it.c, title: it.t, handle: it.h, url: it.u,
             quality: it.q, alternative_codes: it.a || [],
             price_display: it.p, stock_state: it.s, stock_qty: (it.n === undefined ? null : it.n),
             stock_label: stockLabel(it.s), thumb: it.i, match_kind: kind, group: groupOf(kind) };
  }

  // ---- blob load (lazy, non-blocking) ----------------------------------------------------------
  function loadBlob() {
    if (BLOB_STATE === "loading" || BLOB_STATE === "ready") return;
    BLOB_STATE = "loading";
    fetch(BASE + "/v1/suggest/index", { credentials: "same-origin" })
      .then(function (r) { if (!r.ok) throw new Error("blob " + r.status); return r.json(); })
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) throw new Error("bad blob");
        IDX = buildIndex(data.items); BLOB_STATE = "ready";
      })
      .catch(function () { BLOB_STATE = "error"; });          // stay on server fallback
  }

  // ---- server fallback (and pre-blob path) -----------------------------------------------------
  function matchServer(q, signal) {
    return fetch(BASE + "/v1/suggest", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q: q, limit: LIMIT, lang: lang() }),
      credentials: "same-origin", signal: signal
    }).then(function (r) { return r.ok ? r.json() : { results: [] }; })
      .then(function (d) { return (d && d.results) || []; });
  }

  // ---- telemetry (best-effort beacon; never blocks) --------------------------------------------
  function beacon(payload) {
    try {
      fetch(BASE + "/v1/suggest/log", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload), credentials: "same-origin", keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- recent searches (localStorage) ----------------------------------------------------------
  function recent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; } }
  function pushRecent(q) {
    q = (q || "").trim(); if (!q) return;
    var r = recent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    r.unshift(q); try { localStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(0, RECENT_MAX))); } catch (e) {}
  }

  // ---- DOM helpers -----------------------------------------------------------------------------
  function el(tag, attrs, html) {
    var e = document.createElement(tag);
    if (attrs) for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (html != null) e.innerHTML = html;
    return e;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // stock as a unit count: "34 in stock" / "No stock" (falls back to the state label if qty unknown)
  function stockText(n, state) {
    var l = lang();
    if (n != null && n > 0) return n + (l === "nl" ? " op voorraad" : " in stock");
    if (n === 0 || state === "out_of_stock") return l === "nl" ? "Geen voorraad" : "No stock";
    return stockLabel(state);
  }
  // Highlight the matched portion of a code: find the (delimiter-folded) query as a substring of the
  // (delimiter-folded) code, then wrap the corresponding span of the ORIGINAL code — delimiters included —
  // in <b class="rqs-hit">. Returns escaped HTML. So searching "3340" lights up "3340" inside "ERR3340",
  // and "5806hd" lights up "5806H/D" inside "FRC5806H/D". No match (or empty query) -> plain escaped code.
  function highlightCode(codeStr, query) {
    var s = String(codeStr == null ? "" : codeStr);
    var fq = codeKey(query);
    if (!fq) return esc(s);
    var fc = codeKey(s), pos = fc.indexOf(fq);
    if (pos < 0) return esc(s);
    var end = pos + fq.length, fi = 0, a = -1, b = -1;
    for (var i = 0; i < s.length; i++) {
      if (/[A-Za-z0-9]/.test(s[i])) {
        if (fi === pos) a = i;
        if (fi === end - 1) b = i;
        fi++;
      }
    }
    if (a < 0 || b < 0) return esc(s);
    return esc(s.slice(0, a)) + '<b class="rqs-hit">' + esc(s.slice(a, b + 1)) + "</b>" + esc(s.slice(b + 1));
  }
  // "also sold as" codes under the primary; the matched part of each is highlighted (like tier-2).
  // Skips any alt equal to the primary code shown above.
  function altHTML(alts, query, primary) {
    if (!alts || !alts.length) return "";
    var pk = codeKey(primary), out = [];
    for (var i = 0; i < alts.length; i++) {
      if (codeKey(alts[i]) === pk) continue;
      out.push('<span class="rqs-alt">' + highlightCode(alts[i], query) + "</span>");
    }
    return out.length ? '<span class="rqs-alts">' + out.join("") + "</span>" : "";
  }

  // ---- the dropdown controller, one per enhanced input -----------------------------------------
  var UID = 0;
  function enhance(input) {
    if (input.__roverQS) return;
    input.__roverQS = true;
    var id = "rqs-" + (++UID);
    var panel = el("div", { class: "rqs-panel", id: id + "-lb", role: "listbox", "aria-label": "Suggestions" });
    panel.style.display = "none";
    document.body.appendChild(panel);
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", id + "-lb");
    input.setAttribute("autocomplete", "off");

    var rows = [];          // [{type:'option', data}|{type:'all'}] flat, navigable
    var active = -1;
    var lastResults = [];   // for telemetry
    var lastSource = "local";
    var timer = null, ctrl = null, open = false, escalated = false;

    function position() {
      var r = input.getBoundingClientRect();
      panel.style.position = "absolute";
      panel.style.left = (r.left + window.scrollX) + "px";
      panel.style.top = (r.bottom + window.scrollY + 4) + "px";
      panel.style.width = Math.max(r.width, 280) + "px";
      panel.style.zIndex = 99999;
    }
    function show() { if (!open) { open = true; position(); panel.style.display = "block"; input.setAttribute("aria-expanded", "true"); } }
    function hide() {
      if (!open) return;
      open = false; panel.style.display = "none"; input.setAttribute("aria-expanded", "false");
      input.removeAttribute("aria-activedescendant"); active = -1;
    }

    function renderEmptyState() {
      var rec = recent(), pop = (IDX && IDX.popular.slice(0, 6).map(function (i) { return slim(IDX.items[i], "title_token"); })) || [];
      if (!rec.length && !pop.length) { hide(); return; }
      var html = "", flat = [];
      if (rec.length) {
        html += '<div class="rqs-group" role="presentation">' + esc(t("recent")) + "</div>";
        rec.forEach(function (q) {
          html += '<div class="rqs-opt rqs-recent" role="option" id="' + id + "-o" + flat.length + '">' +
                  '<span class="rqs-ico" aria-hidden="true">↩</span><span class="rqs-t">' + esc(q) + "</span></div>";
          flat.push({ type: "recent", q: q });
        });
      }
      if (pop.length) {
        html += '<div class="rqs-group" role="presentation">' + esc(t("popular")) + "</div>";
        pop.forEach(function (p) { html += optHTML(p, flat.length, ""); flat.push({ type: "option", data: p }); });
      }
      panel.innerHTML = html; rows = flat; active = -1; bindRows(); show();
    }

    function optHTML(p, i, query) {
      var thumb = p.thumb ? '<img class="rqs-thumb" src="' + esc(p.thumb) + '" alt="" loading="lazy">'
                          : '<span class="rqs-thumb rqs-thumb-empty" aria-hidden="true"></span>';
      var code = '<span class="rqs-code">' + highlightCode(p.code, query) + "</span>";
      var qual = p.quality ? '<span class="rqs-qual rqs-qual-' + esc(String(p.quality).toLowerCase()) + '">' + esc(p.quality) + "</span>" : "";
      var stock = p.stock_state ? '<span class="rqs-stock rqs-' + esc(p.stock_state) + '">' + esc(stockText(p.stock_qty, p.stock_state)) + "</span>" : "";
      var price = p.price_display ? '<span class="rqs-price">' + esc(p.price_display) + "</span>" : "";
      var alts = altHTML(p.alternative_codes, query, p.code);
      return '<div class="rqs-opt" role="option" id="' + id + "-o" + i + '" data-i="' + i + '">' +
             thumb + '<span class="rqs-body"><span class="rqs-t">' + esc(p.title || p.code) + "</span>" +
             '<span class="rqs-sub">' + code + qual + stock + "</span>" + alts + "</span>" + price + "</div>";
    }

    function renderResults(results, query) {
      lastResults = results; rows = [];
      if (!results.length) {
        // still offer the "search all" escalation row
        panel.innerHTML = allRowHTML(query, 0);
        rows = [{ type: "all", q: query }]; active = -1; bindRows(); show(); return;
      }
      var html = "", flat = [], lastGroup = null;
      results.forEach(function (p) {
        if (p.group !== lastGroup) {
          lastGroup = p.group;
          html += '<div class="rqs-group" role="presentation">' + esc(t(p.group)) + "</div>";
        }
        html += optHTML(p, flat.length, query); flat.push({ type: "option", data: p });
      });
      html += allRowHTML(query, flat.length); flat.push({ type: "all", q: query });
      panel.innerHTML = html; rows = flat; active = -1; bindRows(); show();
    }
    function allRowHTML(query, i) {
      return '<div class="rqs-opt rqs-all" role="option" id="' + id + "-o" + i + '" data-i="' + i + '">' +
             '<span class="rqs-ico" aria-hidden="true">⌕</span><span class="rqs-t">' +
             esc(t("all")) + ' “' + esc(query) + '”</span></div>';
    }

    function bindRows() {
      var opts = panel.querySelectorAll(".rqs-opt");
      for (var i = 0; i < opts.length; i++) {
        (function (node, i) {
          node.addEventListener("mousedown", function (e) { e.preventDefault(); choose(i); });
          node.addEventListener("mouseenter", function () { setActive(i); });
        })(opts[i], i);
      }
    }
    function setActive(i) {
      var opts = panel.querySelectorAll(".rqs-opt");
      if (active >= 0 && opts[active]) opts[active].classList.remove("rqs-active");
      active = i;
      if (i >= 0 && opts[i]) { opts[i].classList.add("rqs-active"); input.setAttribute("aria-activedescendant", opts[i].id); }
      else input.removeAttribute("aria-activedescendant");
    }
    function choose(i) {
      var row = rows[i]; if (!row) return;
      if (row.type === "recent") { input.value = row.q; run(row.q); return; }
      if (row.type === "all") { escalate(row.q); return; }
      var p = row.data;
      pushRecent(input.value);
      beacon({ query_text: input.value, char_count: (input.value || "").length, returned_count: lastResults.length,
               source: lastSource, outcome: "suggestion_click", outcome_item: p.id, match_kind: p.match_kind, lang: lang() });
      if (p.url) window.location.assign(p.url);             // deep-link straight to the product
    }
    function escalate(q) {
      escalated = true; pushRecent(q);
      beacon({ query_text: q, char_count: (q || "").length, returned_count: lastResults.length,
               source: lastSource, outcome: "escalate_search", match_kind: lastResults[0] && lastResults[0].match_kind, lang: lang() });
      hide();
      if (window.Rover && typeof window.Rover.search === "function") window.Rover.search(q);
    }

    function run(query) {
      query = query || "";
      if (query.trim().length < MIN_CHARS) { renderEmptyState(); return; }
      var local = matchLocal(query);
      if (local !== null) { lastSource = "local"; renderResults(local, query); return; }
      // blob not ready -> server fallback, cancel any stale request
      if (ctrl) ctrl.abort();
      ctrl = ("AbortController" in window) ? new AbortController() : null;
      var myCtrl = ctrl;
      matchServer(query, ctrl && ctrl.signal).then(function (results) {
        if (ctrl !== myCtrl) return;                        // a newer request superseded this one
        lastSource = "server"; renderResults(results, query);
      }).catch(function () {});
    }

    input.addEventListener("focus", function () { loadBlob(); if (!input.value.trim()) renderEmptyState(); else run(input.value); });
    input.addEventListener("input", function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { run(input.value); }, DEBOUNCE);
    });
    input.addEventListener("keydown", function (e) {
      if (!open) { if (e.key === "ArrowDown") { run(input.value); } return; }
      var n = rows.length;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(active + 1 >= n ? 0 : active + 1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(active - 1 < 0 ? n - 1 : active - 1); }
      else if (e.key === "Enter") {
        if (active >= 0) { e.preventDefault(); choose(active); }
        else { hide(); }                                    // let the form submit -> tier-2 (logged below)
      } else if (e.key === "Escape") { hide(); }
    });
    input.addEventListener("blur", function () { setTimeout(hide, 120); });

    var form = input.closest && input.closest("form");
    if (form) form.addEventListener("submit", function () {
      if (open && !escalated && rows.length) {
        beacon({ query_text: input.value, char_count: (input.value || "").length, returned_count: lastResults.length,
                 source: lastSource, outcome: "escalate_search", match_kind: lastResults[0] && lastResults[0].match_kind, lang: lang() });
        pushRecent(input.value);
      }
      escalated = false; hide();
    });
    window.addEventListener("scroll", function () { if (open) position(); }, true);
    window.addEventListener("resize", function () { if (open) position(); });
  }

  // ---- delegated init: enhance matching inputs on first focus (covers late-inserted sticky) -----
  function maybeEnhance(node) {
    if (node && node.matches && node.matches(INPUTS)) enhance(node);
  }
  document.addEventListener("focusin", function (e) { maybeEnhance(e.target); }, true);
  // also enhance any already in the DOM at load
  function initExisting() {
    var list = document.querySelectorAll(INPUTS);
    for (var i = 0; i < list.length; i++) enhance(list[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initExisting);
  else initExisting();
})();
